import { Button } from '@shared/ui/button/button.component';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './update-nudge.component.module.css';
import { useVersionCheck } from './version-check.hooks';

/**
 * A quiet, dismissable "a newer build is live" nudge for long-lived tabs. When
 * `useVersionCheck` sees a fresh deploy, this offers a one-tap reload so the tab
 * refreshes before an in-app navigation hits a stale chunk. Renders nothing at
 * rest (so SSR and first client paint agree) and stays gone once dismissed — it
 * doesn't re-pop on the next poll. The stale-chunk recovery (entry.client) is
 * still the safety net if the user ignores it.
 */
export function UpdateNudge() {
	const { t } = useTranslation();
	const updateAvailable = useVersionCheck();
	const [dismissed, setDismissed] = useState(false);

	if (!updateAvailable || dismissed) {
		return null;
	}

	return (
		<div className={styles.nudge} role="status" aria-live="polite">
			<div className={styles.body}>
				<p className={styles.title}>{t('update.title', 'Update available')}</p>
				<p className={styles.description}>
					{t('update.description', 'A newer version is ready. Reload to get it.')}
				</p>
			</div>
			<div className={styles.actions}>
				<Button size="small" variant="primary" onClick={() => window.location.reload()}>
					{t('update.reload', 'Reload')}
				</Button>
				<Button
					size="small"
					variant="ghost"
					onClick={() => setDismissed(true)}
					aria-label={t('update.dismiss', 'Dismiss')}
				>
					{t('update.dismiss', 'Dismiss')}
				</Button>
			</div>
		</div>
	);
}
